/**
 * KimKimTab — Conector Sofi → KimKim (pestaña del viaje).
 *
 * Cuando el viaje tiene `kimkim_trip_url` o `kimkim_code`, esta pestaña
 * SUSTITUYE a TravelDoc + Reconfirmaciones: el TD lo genera KimKim, y aquí el
 * agente recoge las reconfirmaciones de proveedor y las empuja al schedule de
 * KimKim.
 *
 * Portado de `edumorata/Sofi-to-KimKim` (iter 8), adaptado a SOFI 2.0:
 *   - axios via `api` compartido con auth via cookies httpOnly.
 *   - react-query para carga/refetch.
 *   - Estado persistido en `db.trip_kimkim_sync`.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Loader2, RefreshCw, Save, Send, Hotel, ChevronDown, ChevronUp,
  CheckCircle2, XCircle, Shuffle, Unlink, ExternalLink, Link2, Zap, Lock, AlertTriangle, ArrowRight,
} from "lucide-react";
import { api, apiErrorMessage } from "@/lib/api";

// ----------------------------- Helpers ---------------------------------------
const confidenceLabel = (confidence, score) => {
  if (confidence === "manual") return { text: "asignado a mano", cls: "bg-violet-100 text-violet-800" };
  const pct = Math.round((score || 0) * 100);
  const map = {
    alta: "bg-emerald-100 text-emerald-800",
    media: "bg-amber-100 text-amber-800",
    baja: "bg-red-100 text-red-700",
  };
  return { text: `match ${confidence} · ${pct}%`, cls: map[confidence] || "bg-muted" };
};

const kimkimStatusPill = (status) => {
  const s = (status || "").toLowerCase();
  if (s.includes("confirmed"))
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-2.5 py-0.5 text-xs font-medium">
        <CheckCircle2 className="h-3 w-3" /> Confirmed
      </span>
    );
  if (s.includes("not required"))
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-zinc-50 text-zinc-500 border border-zinc-200 px-2.5 py-0.5 text-xs font-medium">
        Not required
      </span>
    );
  if (s.includes("required"))
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200 px-2.5 py-0.5 text-xs font-medium">
        Required
      </span>
    );
  return status ? (
    <span className="inline-flex items-center gap-1 rounded-full bg-muted text-muted-foreground px-2.5 py-0.5 text-xs font-medium">
      {status}
    </span>
  ) : null;
};

const F = ({ id, label, value, onChange, type = "text", hint }) => (
  <div className="space-y-1">
    <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</Label>
    <Input
      data-testid={`kk-field-${id}`}
      type={type}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      className="rounded-md h-8 text-sm"
    />
    {hint && <p className="text-[10px] text-muted-foreground/70">{hint}</p>}
  </div>
);

const matchKey = (m) => m?.kimkim_item?.addon_id || m?.kimkim_item?.new_booking_url || m?.sofi_reservation?.rid || "";
const kkKey = (it) => it?.addon_id || it?.new_booking_url || "";
const UNLINK_VALUE = "__unlink__";

function buildKkOptions(sync, excludeKkKey = "") {
  if (!sync) return [];
  const opts = [];
  (sync.matches || []).forEach((m) => {
    const k = m.kimkim_item;
    const key = kkKey(k);
    if (!key || key === excludeKkKey) return;
    opts.push({
      key,
      title: k.title || "(sin título)",
      day: k.day || "",
      assigned_to: m.sofi_reservation?.service || "",
      assigned_rid: m.sofi_reservation?.rid || "",
    });
  });
  (sync.unmatched_kimkim || []).forEach((k) => {
    const key = kkKey(k);
    if (!key || key === excludeKkKey) return;
    opts.push({ key, title: k.title || "(sin título)", day: k.day || "", assigned_to: "", assigned_rid: "" });
  });
  return opts;
}

// ----------------------------- Rematch dropdown "desde SOFI" -----------------
// Muestra items KimKim como opciones para asignar a un rid SOFI.
function RematchSelect({ sync, rid, currentKkKey, onRematch, disabled, testIdPrefix }) {
  const options = useMemo(() => buildKkOptions(sync, currentKkKey), [sync, currentKkKey]);
  return (
    <Select
      value=""
      onValueChange={(v) => {
        if (!v) return;
        onRematch(rid, v === UNLINK_VALUE ? "" : v);
      }}
      disabled={disabled}
    >
      <SelectTrigger data-testid={`${testIdPrefix}-${rid}`} className="rounded-md h-8 text-xs">
        <SelectValue placeholder={currentKkKey ? "Cambiar item KimKim…" : "Emparejar con KimKim…"} />
      </SelectTrigger>
      <SelectContent>
        {currentKkKey && (
          <SelectItem value={UNLINK_VALUE} data-testid={`${testIdPrefix}-${rid}-unlink`}>
            <span className="flex items-center gap-2 text-red-700">
              <Unlink className="h-3 w-3" /> Romper emparejamiento
            </span>
          </SelectItem>
        )}
        {options.length === 0 && (
          <div className="text-xs text-muted-foreground p-2">No hay items KimKim disponibles</div>
        )}
        {options.map((o) => (
          <SelectItem key={o.key} value={o.key} data-testid={`${testIdPrefix}-${rid}-opt-${o.key}`}>
            <div className="flex flex-col">
              <span className="text-sm">
                {o.day && <span className="text-muted-foreground/70 mr-2">{o.day}</span>}
                {o.title}
              </span>
              {o.assigned_to && (
                <span className="text-[10px] text-amber-700 flex items-center gap-1">
                  <Shuffle className="h-2.5 w-2.5" /> swap con: {o.assigned_to.slice(0, 60)}
                </span>
              )}
            </div>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// ----------------------------- Rematch dropdown "desde KimKim" ---------------
// Muestra reservas SOFI (matched + unmatched) como opciones para asignar a
// este kimkim item.  Cuando la SOFI elegida ya tiene otro KimKim, se hace swap.
function RematchSelectByKk({ sync, kkKey, onRematch, rematching }) {
  const options = useMemo(() => {
    if (!sync) return [];
    const out = [];
    (sync.matches || []).forEach((m) => {
      const r = m.sofi_reservation;
      const currentKk = m.kimkim_item;
      out.push({
        rid: r.rid, label: r.service, date: r.iso_date || r.start_date,
        assigned_to_kk: currentKk?.title || "",
      });
    });
    (sync.unmatched_sofi || []).forEach((r) => {
      out.push({ rid: r.rid, label: r.service, date: r.iso_date || r.start_date, assigned_to_kk: "" });
    });
    return out;
  }, [sync]);

  return (
    <Select
      value=""
      onValueChange={(rid) => { if (rid) onRematch(rid, kkKey); }}
      disabled={!!rematching}
    >
      <SelectTrigger data-testid={`kk-only-rematch-${kkKey}`} className="rounded-md h-8 text-xs">
        <SelectValue placeholder="Emparejar con reserva SOFI…" />
      </SelectTrigger>
      <SelectContent>
        {options.length === 0 && (
          <div className="text-xs text-muted-foreground p-2">No hay reservas SOFI disponibles</div>
        )}
        {options.map((o) => (
          <SelectItem key={`opt-${o.rid}`} value={o.rid} data-testid={`kk-only-rematch-${kkKey}-opt-${o.rid}`}>
            <div className="flex flex-col">
              <span className="text-sm">
                {o.date && <span className="text-muted-foreground/70 mr-2">{o.date}</span>}
                {o.label}
              </span>
              {o.assigned_to_kk && (
                <span className="text-[10px] text-amber-700 flex items-center gap-1">
                  <Shuffle className="h-2.5 w-2.5" /> swap: hoy va con «{o.assigned_to_kk.slice(0, 40)}»
                </span>
              )}
            </div>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}


// ----------------------------- Match card ------------------------------------
function MatchCard({ row, onChange, sync, onRematch, rematching }) {
  const [open, setOpen] = useState(false);
  const r = row.sofi_reservation;
  const k = row.kimkim_item;
  const setField = (key) => (val) => onChange({ fields: { ...row.fields, [key]: val } });
  const key = matchKey(row);
  const pushed = row.push_result?.status;
  const isHotel = !!row.is_hotel;
  const needsResend = !!row.needs_resend;

  // Estilos condicionales: verde=enviado OK, ámbar=pendiente de reenvío tras
  // editar la plantilla, rojo=error del último push, defecto=aún sin enviar.
  let borderCls = "border-border bg-card";
  if (needsResend) borderCls = "border-amber-300 bg-amber-50/50";
  else if (pushed === "success") borderCls = "border-emerald-300 bg-emerald-50/40";
  else if (pushed === "error") borderCls = "border-red-300 bg-red-50/40";

  return (
    <div className={`rounded-md border ${borderCls}`} data-testid={`kk-match-row-${key}`}>
      <div className="flex items-center gap-3 p-3">
        <Checkbox
          data-testid={`kk-match-checkbox-${key}`}
          checked={!!row.checked}
          disabled={!row.pushable}
          onCheckedChange={(v) => onChange({ checked: !!v })}
        />
        <div className="flex-1 min-w-0 grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] items-center gap-2">
          <div className="min-w-0">
            <p className="text-sm font-semibold truncate">{r.service}</p>
            <p className="text-xs text-muted-foreground truncate">
              SOFI #{r.rid} · {r.iso_date || r.start_date} {r.meeting_time || ""}
            </p>
          </div>
          <span className="text-primary text-sm hidden md:inline">→</span>
          <div className="min-w-0">
            <p className="text-sm font-semibold truncate flex items-center gap-1">
              {isHotel && <Hotel className="h-3.5 w-3.5 text-violet-600 shrink-0" />}
              <span className="truncate">{k.title}</span>
            </p>
            {!row.pushable && !k.new_booking_url && (
              <p className="text-xs text-muted-foreground truncate italic">ya confirmado o reservado por KimKim</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {needsResend ? (
            <span
              data-testid={`kk-needs-resend-${key}`}
              className="inline-flex items-center gap-1 rounded-full border border-amber-400 bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-800"
              title="La plantilla se ha modificado desde el último envío. Selecciónalo y pulsa «Confirmar seleccionados» para actualizar KimKim."
            >
              <AlertTriangle className="h-3 w-3" /> cambios sin enviar
            </span>
          ) : pushed === "error" ? (
            <span
              className="flex items-center gap-1 text-red-600 text-xs font-semibold"
              title={row.push_result.error}
            >
              <XCircle className="h-4 w-4" /> Error último push
            </span>
          ) : (
            // Estado principal = lo que KimKim tiene. Tras un push exitoso
            // desde SOFI mostramos "Confirmed" (que es lo que KimKim registra
            // automáticamente al recibir la confirmación), evitando ambigüedad
            // con el legacy «Enviado».
            kimkimStatusPill(
              pushed === "success" ? "Confirmed" : k.status
            )
          )}
          {pushed === "success" && !needsResend && (
            <span
              className="text-[10px] text-emerald-700/70 font-medium hidden md:inline"
              title={`Enviado desde SOFI · ${row.push_result?.at ? new Date(row.push_result.at).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" }) : ""}`}
            >
              enviado
            </span>
          )}
          <button
            type="button"
            data-testid={`kk-match-expand-${key}`}
            onClick={() => setOpen(!open)}
            className="p-1 rounded hover:bg-muted"
          >
            {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {open && (
        <div
          className="border-t border-border p-3 grid grid-cols-2 md:grid-cols-4 gap-3"
          data-testid={`kk-match-details-${key}`}
        >
          <div className="col-span-2 md:col-span-4 flex items-center gap-2 pb-1 -mt-1">
            {(() => {
              const c = confidenceLabel(row.confidence, row.score);
              return <span className={`inline-flex rounded-md px-2 py-0.5 text-[10px] font-medium ${c.cls}`}>{c.text}</span>;
            })()}
            {kimkimStatusPill(k.status)}
          </div>
          <F id="confirmation_number" label="Confirmation #" value={row.fields?.confirmation_number} onChange={setField("confirmation_number")} />
          <F id="meeting_time" label="Hora inicio" type="time" value={row.fields?.meeting_time} onChange={setField("meeting_time")} />
          <F id="end_time" label="Hora fin" type="time" value={row.fields?.end_time} onChange={setField("end_time")} />
          <F id="operator_name" label="Operador (no se envía)" value={row.fields?.operator_name} onChange={setField("operator_name")} hint="Queda en blanco a propósito" />
          <F id="departure_location" label="Salida" value={row.fields?.departure_location} onChange={setField("departure_location")} />
          <F id="arrival_location" label="Llegada" value={row.fields?.arrival_location} onChange={setField("arrival_location")} />
          <div className="col-span-2 space-y-1">
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1">
              Notas para el viajero
              <span className="normal-case tracking-normal text-[10px] text-muted-foreground/70 font-normal">
                · vienen de la plantilla, edita ahí para cambiarlas
              </span>
            </Label>
            <Textarea
              data-testid={`kk-match-field-notes-${key}`}
              rows={4}
              value={row.fields?.notes || ""}
              readOnly
              className="rounded-md text-xs font-mono bg-muted/40 cursor-not-allowed"
            />
          </div>
          <div className="col-span-2 md:col-span-4 space-y-1 border-t border-border pt-3 mt-1">
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1">
              <Shuffle className="h-3 w-3" /> Cambiar emparejamiento manualmente
            </Label>
            <RematchSelect
              sync={sync}
              rid={r.rid}
              currentKkKey={kkKey(k)}
              onRematch={onRematch}
              disabled={rematching === r.rid}
              testIdPrefix="kk-match-rematch"
            />
            <p className="text-[10px] text-muted-foreground/70">
              Selecciona el item correcto: si ya tiene otra reserva asignada se hará swap automático.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// ===================== Componente principal ==================================
export default function KimKimTab({ tripId, trip }) {
  const qc = useQueryClient();
  const [rows, setRows] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [rematching, setRematching] = useState(null);
  const [urlInput, setUrlInput] = useState(trip?.kimkim_trip_url || "");
  const [editingUrl, setEditingUrl] = useState(false);

  const syncQ = useQuery({
    queryKey: ["kk-sync", tripId],
    queryFn: async () => (await api.get(`/trips/${tripId}/kimkim/sync`)).data,
  });
  const sync = syncQ.data;

  // Hidrata `rows` a partir de sync.matches (una vez cargados)
  useEffect(() => {
    if (!sync) return;
    setRows((sync.matches || []).map((m) => ({
      ...m,
      // Por defecto NADA seleccionado. El agente decide qué enviar mediante
      // los botones de selección masiva o pulsando los checkboxes uno a uno.
      checked: false,
    })));
    setDirty(false);
  }, [sync]);

  // ---- polling del push_job en curso ----
  const pushJob = sync?.push_job;
  const isPushing = pushJob?.status === "running";
  useEffect(() => {
    if (!isPushing) return;
    const t = setInterval(() => {
      api.get(`/trips/${tripId}/kimkim/status`).then((r) => {
        qc.setQueryData(["kk-sync", tripId], (old) => ({
          ...(old || {}), push_job: r.data.push_job, matches: r.data.matches,
        }));
        if (r.data.push_job?.status === "done") {
          const { total = 0, errors = 0 } = r.data.push_job;
          if (errors === 0) toast.success(`${total} confirmaciones enviadas a KimKim`);
          else toast.warning(`${total - errors} enviadas, ${errors} con error`);
          clearInterval(t);
        }
      }).catch(() => {});
    }, 2000);
    return () => clearInterval(t);
  }, [isPushing, tripId, qc]);

  // ---- mutations ----
  const matchMut = useMutation({
    mutationFn: async (kimkim_url) =>
      (await api.post(`/trips/${tripId}/kimkim/match`, { kimkim_url })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["kk-sync", tripId] }); toast.success("Empareamiento hecho"); },
    onError: (e) => toast.error(apiErrorMessage(e, "No se pudo emparejar")),
  });

  const reanalyzeMut = useMutation({
    mutationFn: async () =>
      (await api.post(`/trips/${tripId}/kimkim/reanalyze`, { kimkim_url: urlInput })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["kk-sync", tripId] }); toast.success("Re-analizado"); },
    onError: (e) => toast.error(apiErrorMessage(e, "Error re-analizando")),
  });

  const saveMut = useMutation({
    mutationFn: async () =>
      (await api.put(`/trips/${tripId}/kimkim/overrides`, {
        matches: rows.map((r) => ({
          addon_id: r.kimkim_item.addon_id,
          new_booking_url: r.kimkim_item.new_booking_url,
          fields: r.fields,
        })),
      })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["kk-sync", tripId] }); toast.success("Cambios guardados"); },
    onError: (e) => toast.error(apiErrorMessage(e, "Error guardando")),
  });

  const rematchMut = useMutation({
    mutationFn: async ({ rid, new_kk_key }) =>
      (await api.post(`/trips/${tripId}/kimkim/rematch`, { rid, new_kk_key })).data,
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["kk-sync", tripId] });
      toast.success(vars.new_kk_key ? "Emparejamiento actualizado" : "Emparejamiento roto");
    },
    onError: (e) => toast.error(apiErrorMessage(e, "No se pudo cambiar el emparejamiento")),
    onSettled: () => setRematching(null),
  });

  const applyFlightMut = useMutation({
    mutationFn: async (type) =>
      (await api.post(`/trips/${tripId}/kimkim/apply-flight-fix`, { type })).data,
    onSuccess: (_data, type) => {
      qc.invalidateQueries({ queryKey: ["kk-sync", tripId] });
      qc.invalidateQueries({ queryKey: ["trip", tripId] });
      toast.success(`Vuelo de ${type === "arrival" ? "llegada" : "salida"} actualizado desde KimKim`);
    },
    onError: (e) => toast.error(apiErrorMessage(e, "No se pudo actualizar el vuelo")),
  });

  const pushMut = useMutation({
    mutationFn: async ({ selected_keys, dry_run }) =>
      (await api.post(`/trips/${tripId}/kimkim/push-async`, { selected_keys, dry_run })).data,
    onSuccess: (data) => {
      if (data.dry_run) {
        const ok = data.results.filter((r) => r.ok).length;
        toast.success(`Dry-run OK: ${ok}/${data.results.length} items listos para enviar`);
      } else {
        toast.info(`Enviando ${data.total} confirmaciones a KimKim…`);
        qc.invalidateQueries({ queryKey: ["kk-sync", tripId] });
      }
    },
    onError: (e) => toast.error(apiErrorMessage(e, "Error lanzando el envío")),
  });

  // ---- handlers ----
  const handleRematch = useCallback((rid, new_kk_key) => {
    if (dirty && !window.confirm("Tienes cambios sin guardar. ¿Continuar y perderlos?")) return;
    setRematching(rid);
    rematchMut.mutate({ rid, new_kk_key });
  }, [dirty, rematchMut]);

  const updateRow = (key, patch) => {
    setRows((rs) => rs.map((r) => (matchKey(r) === key ? { ...r, ...patch } : r)));
    if (patch.fields) setDirty(true);
  };

  // ===== Timeline cronológico unificado =====
  // Mezcla matches + KimKim sin match (bookings pendientes + booked-by-KimKim)
  // ordenados por su posición en la agenda KimKim.  SOFI sin match va aparte.
  const timeline = useMemo(() => {
    if (!sync) return [];
    const items = [];
    // Cada row en el estado local `rows` tiene el kk_index del kimkim_item
    rows.forEach((row) => {
      items.push({
        kind: "match",
        row,
        kk_index: row.kimkim_item?.kk_index ?? 999,
        key: matchKey(row),
      });
    });
    (sync.unmatched_kimkim || []).forEach((it) => {
      const isBooked = !it.new_booking_url;
      items.push({
        kind: isBooked ? "booked" : "kk-only",
        kimkim_item: it,
        kk_index: it.kk_index ?? 999,
        key: kkKey(it) || `booked-${it.kk_index}`,
      });
    });
    // Day-headers (banda gris entre items) — kk_index es la posición global
    (sync.day_headers || []).forEach((h) => {
      items.push({
        kind: "day_header",
        header: h,
        kk_index: h.kk_index ?? 999,
        key: `dh-${h.kk_index}`,
      });
    });
    items.sort((a, b) => (a.kk_index - b.kk_index) || (a.key > b.key ? 1 : -1));
    return items;
  }, [rows, sync]);

  const checkedRows = useMemo(() => rows.filter((r) => r.checked && r.pushable), [rows]);
  const pushableRows = useMemo(() => rows.filter((r) => r.pushable), [rows]);
  const pendingRows = useMemo(() => rows.filter((r) => r.pushable && (r.needs_resend || !r.push_result || r.push_result.status !== "success")), [rows]);
  const needsResendCount = useMemo(() => rows.filter((r) => r.needs_resend).length, [rows]);

  const selectAll = () => setRows((rs) => rs.map((r) => (r.pushable ? { ...r, checked: true } : r)));
  const selectNone = () => setRows((rs) => rs.map((r) => ({ ...r, checked: false })));
  const selectPending = () => setRows((rs) => rs.map((r) => ({
    ...r,
    checked: r.pushable && (r.needs_resend || !r.push_result || r.push_result.status !== "success"),
  })));


  const push = async (dry_run) => {
    if (checkedRows.length === 0) return;
    if (dirty && !dry_run) {
      if (!window.confirm("Tienes cambios sin guardar. ¿Guardar antes de enviar?")) return;
      await saveMut.mutateAsync();
    }
    const selected_keys = checkedRows.map((r) => r.kimkim_item.addon_id || r.kimkim_item.new_booking_url);
    pushMut.mutate({ selected_keys, dry_run });
  };

  // ---- render ----
  if (syncQ.isLoading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  }

  const hasData = (sync?.matches || []).length > 0;
  const canMatch = !!urlInput.trim();

  return (
    <div className="space-y-4" data-testid="kimkim-tab">
      {/* ------ Header: metadata + botones (compacto cuando ya hay datos) ------ */}
      <Card className="shadow-none border-border">
        <CardHeader className="pb-3 border-b border-border">
          {(!hasData || editingUrl) ? (
            // Modo inicial / editando URL: input completo
            <div className="flex flex-wrap items-end gap-3">
              <div className="flex-1 min-w-[280px] space-y-1">
                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                  <Link2 className="h-3 w-3" />
                  URL del viaje en KimKim
                </Label>
                <Input
                  data-testid="kk-url-input"
                  value={urlInput}
                  onChange={(e) => setUrlInput(e.target.value)}
                  placeholder="https://www.kimkim.com/tp/XXXXXXXX/schedule"
                  className="rounded-md h-9 text-sm font-mono"
                />
              </div>
              <Button
                data-testid="kk-match-button"
                onClick={() => { matchMut.mutate(urlInput); setEditingUrl(false); }}
                disabled={!canMatch || matchMut.isPending}
                className="rounded-md"
              >
                {matchMut.isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Zap className="h-4 w-4 mr-1" />}
                {hasData ? "Re-emparejar con nueva URL" : "Emparejar"}
              </Button>
              {hasData && (
                <Button variant="ghost" size="sm" onClick={() => setEditingUrl(false)} className="rounded-md">
                  Cancelar
                </Button>
              )}
            </div>
          ) : (
            // Modo con datos: metadata compacta + botones de acción
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
                <Link2 className="h-3 w-3" />
                <a
                  href={sync.kimkim_trip_url}
                  target="_blank" rel="noreferrer"
                  className="font-mono underline decoration-dotted underline-offset-2 hover:text-foreground"
                  data-testid="kk-url-link"
                >
                  {sync.kimkim_trip_url}
                </a>
                <span className="text-muted-foreground/60">·</span>
                <span>Código KimKim: <span className="font-mono font-semibold text-foreground">{sync.kimkim_trip_code}</span></span>
                {sync.updated_at && (
                  <>
                    <span className="text-muted-foreground/60">·</span>
                    <span>último cambio: {new Date(sync.updated_at).toLocaleString("es-ES", { dateStyle: "medium", timeStyle: "short" })}</span>
                  </>
                )}
                <Button variant="ghost" size="sm" onClick={() => setEditingUrl(true)} className="h-6 px-2 text-xs" data-testid="kk-edit-url">
                  Cambiar URL
                </Button>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Button
                  data-testid="kk-reanalyze-button"
                  variant="outline" size="sm"
                  onClick={() => reanalyzeMut.mutate()}
                  disabled={reanalyzeMut.isPending || isPushing}
                  className="rounded-full"
                >
                  {reanalyzeMut.isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
                  Re-analizar
                </Button>
                <Button
                  data-testid="kk-save-button"
                  variant="outline" size="sm"
                  onClick={() => saveMut.mutate()}
                  disabled={saveMut.isPending || !dirty || isPushing}
                  className={`rounded-full ${dirty ? "border-amber-400 text-amber-700" : ""}`}
                >
                  {saveMut.isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
                  {dirty ? "Guardar" : "Guardado"}
                </Button>
                <Button
                  data-testid="kk-dryrun-button"
                  variant="outline" size="sm"
                  onClick={() => push(true)}
                  disabled={pushMut.isPending || checkedRows.length === 0 || isPushing}
                  className="rounded-full"
                  title="Muestra qué se enviaría a KimKim, sin escribir nada"
                >
                  Simular ({checkedRows.length})
                </Button>
                <Button
                  data-testid="kk-push-button"
                  size="sm"
                  onClick={() => push(false)}
                  disabled={pushMut.isPending || checkedRows.length === 0 || isPushing}
                  className="rounded-full font-semibold"
                >
                  {isPushing ? (
                    <span className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Enviando…</span>
                  ) : (
                    <span className="flex items-center gap-2"><Send className="h-4 w-4" /> Confirmar seleccionados ({checkedRows.length})</span>
                  )}
                </Button>
              </div>
            </div>
          )}
        </CardHeader>

        {/* ------ Progress banner ------ */}
        {isPushing && (
          <div data-testid="kk-push-progress-banner" className="border-b border-border bg-blue-50/60 p-4 space-y-2">
            <div className="flex items-center gap-3">
              <Loader2 className="h-5 w-5 animate-spin text-blue-700 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm text-blue-900">
                  Enviando confirmaciones a KimKim
                  <span className="ml-2 font-mono text-blue-700">
                    {pushJob.processed} / {pushJob.total}
                  </span>
                  {pushJob.errors > 0 && (
                    <span className="ml-2 text-red-600 text-xs font-semibold">
                      ({pushJob.errors} con error)
                    </span>
                  )}
                </p>
                {pushJob.current_item && (
                  <p className="text-xs text-blue-800 truncate">
                    Procesando ahora: <span className="font-mono">{pushJob.current_item}</span>
                  </p>
                )}
              </div>
              <span className="text-2xl font-bold text-blue-900 tabular-nums">
                {pushJob.total ? Math.round((pushJob.processed / pushJob.total) * 100) : 0}%
              </span>
            </div>
            <div className="h-2 w-full bg-blue-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-600 transition-all duration-300 ease-out"
                style={{ width: `${pushJob.total ? Math.round((pushJob.processed / pushJob.total) * 100) : 0}%` }}
              />
            </div>
          </div>
        )}

        <CardContent className="pt-3 space-y-2">
          {!hasData && (
            <div className="py-10 text-center text-muted-foreground text-sm" data-testid="kk-empty">
              Aún no has emparejado el viaje. Pega la URL de KimKim arriba (`/tp/CODE/schedule`) y pulsa <span className="font-semibold">Emparejar</span>.
            </div>
          )}

          {/* -- Alertas de discrepancia de vuelos con Datos SOFI -- */}
          {hasData && (sync?.flight_alerts || []).map((alert) => {
            const isArrival = alert.type === "arrival";
            const sofiLabel = isArrival ? "Vuelo de llegada" : "Vuelo de salida";
            const kkLabel = isArrival ? "Arrival" : "Departure";
            return (
              <div
                key={`flight-alert-${alert.type}`}
                data-testid={`kk-flight-alert-${alert.type}`}
                className={`flex items-start gap-3 rounded-md border p-3 ${
                  alert.level === "warning"
                    ? "border-amber-300 bg-amber-50 text-amber-900"
                    : "border-sky-300 bg-sky-50 text-sky-900"
                }`}
              >
                <AlertTriangle className={`h-5 w-5 shrink-0 mt-0.5 ${alert.level === "warning" ? "text-amber-600" : "text-sky-600"}`} />
                <div className="flex-1 min-w-0 space-y-2">
                  <p className="text-sm font-semibold">
                    Discrepancia en el vuelo de {isArrival ? "llegada" : "salida"}
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                    <div className="rounded border border-current/20 bg-white/40 p-2">
                      <p className="font-semibold text-[10px] uppercase tracking-wider opacity-70 mb-0.5">
                        Datos SOFI ({sofiLabel})
                      </p>
                      {alert.sofi_value
                        ? <pre className="font-mono text-xs whitespace-pre-wrap m-0">{alert.sofi_value}</pre>
                        : <p className="italic opacity-60 m-0">(vacío)</p>}
                    </div>
                    <div className="rounded border border-current/20 bg-white/40 p-2">
                      <p className="font-semibold text-[10px] uppercase tracking-wider opacity-70 mb-0.5">
                        Schedule KimKim ({kkLabel})
                      </p>
                      <pre className="font-mono text-xs whitespace-pre-wrap m-0">{alert.sofi_new_value || alert.kimkim_value}</pre>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 pt-1">
                    <Button
                      size="sm"
                      data-testid={`kk-flight-apply-${alert.type}`}
                      onClick={() => applyFlightMut.mutate(alert.type)}
                      disabled={applyFlightMut.isPending}
                      className="rounded-full h-7 text-xs font-semibold"
                    >
                      {applyFlightMut.isPending
                        ? <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                        : <ArrowRight className="h-3 w-3 mr-1" />}
                      Usar el de KimKim y actualizar SOFI
                    </Button>
                    {alert.sofi_value && trip?.kimkim_trip_url && (
                      <Button
                        variant="ghost" size="sm" asChild
                        data-testid={`kk-flight-open-kk-${alert.type}`}
                        className="rounded-full h-7 text-xs"
                      >
                        <a href={trip.kimkim_trip_url} target="_blank" rel="noreferrer">
                          <ExternalLink className="h-3 w-3 mr-1" />
                          Corregir en KimKim (manual)
                        </a>
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          {/* Barra de selección masiva */}
          {hasData && pushableRows.length > 0 && (
            <div
              data-testid="kk-select-bar"
              className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border bg-muted/30 px-3 py-2"
            >
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span className="font-semibold text-foreground">
                  {checkedRows.length}
                </span>
                <span>de {pushableRows.length} seleccionados</span>
                {needsResendCount > 0 && (
                  <span className="ml-2 inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-800">
                    <AlertTriangle className="h-2.5 w-2.5" />
                    {needsResendCount} con cambios sin enviar
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {needsResendCount > 0 && (
                  <Button
                    data-testid="kk-select-pending"
                    variant="outline"
                    size="sm"
                    onClick={selectPending}
                    className="rounded-full h-7 text-xs"
                  >
                    Solo pendientes ({pendingRows.length})
                  </Button>
                )}
                <Button
                  data-testid="kk-select-all"
                  variant="outline"
                  size="sm"
                  onClick={selectAll}
                  className="rounded-full h-7 text-xs"
                  disabled={checkedRows.length === pushableRows.length}
                >
                  Seleccionar todos
                </Button>
                <Button
                  data-testid="kk-select-none"
                  variant="ghost"
                  size="sm"
                  onClick={selectNone}
                  className="rounded-full h-7 text-xs"
                  disabled={checkedRows.length === 0}
                >
                  Deseleccionar
                </Button>
              </div>
            </div>
          )}

          {timeline.map((tl) => {
            if (tl.kind === "day_header") {
              const h = tl.header;
              return (
                <div
                  key={tl.key}
                  data-testid={`kk-day-header-${h.kk_index}`}
                  className="flex items-baseline gap-6 rounded-md bg-zinc-100/80 px-4 py-2.5 mt-1"
                >                  <div className="min-w-[120px]">
                    <p className="text-sm font-bold text-zinc-900">{h.day_label || "Día"}</p>
                    {h.day_date && <p className="text-[10px] uppercase tracking-wider text-zinc-500">{h.day_date}</p>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-zinc-900">{h.day_city || ""}</p>
                    {h.day_meals && <p className="text-xs text-zinc-500">Meals included: {h.day_meals}</p>}
                  </div>
                </div>
              );
            }
            if (tl.kind === "match") {
              return (
                <MatchCard
                  key={`m-${tl.key}`}
                  row={tl.row}
                  onChange={(patch) => updateRow(matchKey(tl.row), patch)}
                  sync={sync}
                  onRematch={handleRematch}
                  rematching={rematching}
                />
              );
            }
            if (tl.kind === "booked") {
              const it = tl.kimkim_item;
              const fl = it.flight;
              const isTraveler = !!fl;
              return (
                <div
                  key={`b-${tl.key}`}
                  data-testid={`kk-booked-${tl.key}`}
                  className={`rounded-md border p-3 ${isTraveler ? "border-emerald-200 bg-emerald-50/40" : "border-sky-200 bg-sky-50/50"}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`h-6 w-6 shrink-0 rounded-full flex items-center justify-center ${isTraveler ? "bg-emerald-100" : "bg-sky-100"}`}>
                      <Lock className={`h-3.5 w-3.5 ${isTraveler ? "text-emerald-700" : "text-sky-700"}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-semibold truncate flex items-center gap-2 ${isTraveler ? "text-emerald-900" : "text-sky-900"}`}>
                        {it.title}
                        {fl?.time && <span className={`text-xs font-normal ${isTraveler ? "text-emerald-800/70" : "text-sky-800/70"}`}>· {fl.time}</span>}
                      </p>
                    </div>
                    <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-semibold shrink-0 ${
                      isTraveler
                        ? "bg-emerald-100 text-emerald-800 border-emerald-300"
                        : "bg-sky-100 text-sky-800 border-sky-300"
                    }`}>
                      <CheckCircle2 className="h-3 w-3" />
                      {isTraveler ? "Booked by traveler" : "Booked by KimKim"}
                    </span>
                  </div>
                  {fl && (fl.flight || fl.airport || fl.route) && (
                    <div className="mt-2 ml-9 grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1 text-xs">
                      {fl.flight && <div><span className="text-emerald-700/60">Vuelo:</span> <span className="font-semibold text-emerald-900">{fl.flight}</span></div>}
                      {fl.airport && <div><span className="text-emerald-700/60">{fl.type === "departure" ? "Aeropuerto salida" : "Aeropuerto llegada"}:</span> <span className="font-semibold text-emerald-900">{fl.airport}</span></div>}
                      {fl.route && <div><span className="text-emerald-700/60">Ruta:</span> <span className="font-semibold text-emerald-900">{fl.route}</span></div>}
                    </div>
                  )}
                </div>
              );
            }
            // kk-only: item KimKim con URL pero sin SOFI emparejado
            const it = tl.kimkim_item;
            return (
              <div
                key={`k-${tl.key}`}
                data-testid={`kk-only-${tl.key}`}
                className="grid grid-cols-1 md:grid-cols-[1fr_320px] gap-3 items-center p-3 rounded-md border border-amber-200 bg-amber-50/40"
              >
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-amber-950 truncate">{it.title}</p>
                  <p className="text-xs text-amber-900/70 truncate italic">sin reserva SOFI</p>
                </div>
                <div className="flex items-center gap-2">
                  <RematchSelectByKk
                    sync={sync}
                    kkKey={tl.key}
                    onRematch={handleRematch}
                    rematching={rematching}
                  />
                </div>
              </div>
            );
          })}

          {sync?.unmatched_sofi?.length > 0 && (
            <div className="mt-6 rounded-md border border-dashed border-border p-3 space-y-2" data-testid="kk-unmatched-section">
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Reservas SOFI sin item KimKim</p>
              {sync.unmatched_sofi.map((res) => (
                <div
                  key={`uns-${res.rid}`}
                  data-testid={`kk-unmatched-sofi-${res.rid}`}
                  className="grid grid-cols-1 md:grid-cols-[1fr_320px] gap-2 items-center p-2 rounded bg-muted/30"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate flex items-center gap-1">
                      {res.is_hotel && <Hotel className="h-3.5 w-3.5 text-violet-600 shrink-0" />}
                      {res.service}
                    </p>
                    <p className="text-[11px] text-muted-foreground">
                      SOFI #{res.rid} · {res.iso_date || res.start_date}{res.meeting_time && ` ${res.meeting_time}`}
                    </p>
                  </div>
                  <RematchSelect
                    sync={sync}
                    rid={res.rid}
                    currentKkKey=""
                    onRematch={handleRematch}
                    disabled={rematching === res.rid}
                    testIdPrefix="kk-unmatched-rematch"
                  />
                </div>
              ))}
            </div>
          )}

          {sync?.kimkim_sourced?.length > 0 && (
            <div className="mt-4 rounded-md border border-violet-200 bg-violet-50/40 p-3 space-y-2" data-testid="kk-sourced-section">
              <p className="text-xs uppercase tracking-wider text-violet-700 font-semibold flex items-center gap-1">
                <Hotel className="h-3.5 w-3.5" />
                Hoteles reservados desde KimKim — omitidos
              </p>
              <p className="text-[11px] text-violet-800/80">
                Estos hoteles tienen a «KimKim» como proveedor en SOFI: la información ya viene directamente de la plataforma, así que no se emparejan ni se envían.
              </p>
              {sync.kimkim_sourced.map((res) => (
                <div
                  key={`kks-${res.rid}`}
                  data-testid={`kk-sourced-${res.rid}`}
                  className="flex items-center gap-2 p-2 rounded bg-white/60 border border-violet-100"
                >
                  <Hotel className="h-3.5 w-3.5 text-violet-600 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{res.service}</p>
                    <p className="text-[11px] text-violet-800/70">
                      SOFI #{res.rid} · {res.iso_date || res.start_date}
                      {res.operator_name && ` · proveedor: ${res.operator_name}`}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
